/*
 * auth (provider)
 */
import { appConfig } from "@emr/config"
import { toast } from "@emr/react-ui"
import { AppPlatform } from "@emr/utils"
import { Session } from "@supabase/supabase-js"
import { useNavigate } from "@tanstack/react-router"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { AuthContext } from "./auth.context"
import supabase from "./services/supabase"

type AuthProviderProps = {
	children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
	const navigate = useNavigate()
	const { t } = useTranslation()

	const [session, setSession] = useState<Session | null>(null)
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		supabase.auth.getSession().then(({ data: { session } }) => {
			setSession(session)
			setIsLoading(false)
		})

		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange(async (_, session) => {
			setSession(session)
			setIsLoading(false)
		})

		return () => {
			subscription.unsubscribe()
		}
	}, [])

	// Poll for approval status when the user is authenticated but not yet approved.
	useEffect(() => {
		const approvalUrl = appConfig.redirects.afterWaitlistApprovalUrl
		if (!approvalUrl || !session) {
			return
		}
		if (session.user.user_metadata[AppPlatform]) {
			return
		}

		const interval = setInterval(async () => {
			const { data } = await supabase.auth.refreshSession()
			if (data.session?.user.user_metadata[AppPlatform]) {
				clearInterval(interval)
				toast.success(t("auth.waitlistApproved"))
				navigate({ to: approvalUrl })
			}
		}, appConfig.misc?.approvalPollIntervalMs ?? 30_000)

		return () => clearInterval(interval)
	}, [session])

	async function signOut() {
		await supabase.auth.signOut()
	}

	return (
		<AuthContext.Provider
			value={{ session, user: session?.user ?? null, signOut, isLoading }}
		>
			{children}
		</AuthContext.Provider>
	)
}
